import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { notify } from '@nimling/components-nimtech'
import type { View, PageView } from '@/types'
import * as api from '@/lib/api'

export const useViewsStore = defineStore('views', () => {
  const isLoading = ref(false)
  const viewIndex = ref<Record<string, View>>({})
  const viewIds = ref<string[]>([])
  const pageViewIndex = ref<Record<string, PageView>>({})
  const pageViewIds = ref<Record<string, string[]>>({})
  const dragId = ref<string | null>(null)
  const dropBox = ref({ width: 0, height: 0 })

  const views = computed(() => viewIds.value.map(id => viewIndex.value[id]).filter(Boolean))

  const pageViews = computed(() => {
    const out: Record<string, PageView[]> = {}
    for (const [pageId, ids] of Object.entries(pageViewIds.value)) {
      out[pageId] = ids.map(id => pageViewIndex.value[id]).filter(Boolean)
    }
    return out
  })

  const ingestView = (view: View) => {
    const known = !!viewIndex.value[view.id]
    viewIndex.value = { ...viewIndex.value, [view.id]: view }
    if (!known) viewIds.value = [...viewIds.value, view.id]
  }

  const removeView = (id: string) => {
    if (!viewIndex.value[id]) return
    const next = { ...viewIndex.value }
    delete next[id]
    viewIndex.value = next
    viewIds.value = viewIds.value.filter(x => x !== id)
  }

  const ingestPageView = (sv: PageView) => {
    const prev = pageViewIndex.value[sv.id]
    pageViewIndex.value = { ...pageViewIndex.value, [sv.id]: sv }
    const byPage = { ...pageViewIds.value }
    if (prev && prev.page_id !== sv.page_id) {
      byPage[prev.page_id] = (byPage[prev.page_id] || []).filter(x => x !== sv.id)
    }
    const list = byPage[sv.page_id] || []
    if (!list.includes(sv.id)) byPage[sv.page_id] = [...list, sv.id]
    pageViewIds.value = byPage
  }

  const removePageView = (id: string) => {
    const prev = pageViewIndex.value[id]
    if (!prev) return
    const next = { ...pageViewIndex.value }
    delete next[id]
    pageViewIndex.value = next
    pageViewIds.value = {
      ...pageViewIds.value,
      [prev.page_id]: (pageViewIds.value[prev.page_id] || []).filter(x => x !== id)
    }
  }

  const getViews = async (): Promise<View[]> => {
    isLoading.value = true
    try {
      const rows = await api.getViews()
      for (const v of rows) ingestView(v)
      return rows
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load views')
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const getView = async (id: string): Promise<View> => {
    const view = await api.getView(id)
    ingestView(view)
    return view
  }

  const postView = async (input: Partial<View>): Promise<View> => {
    const created = await api.postView(input)
    ingestView(created)
    notify('success', `View "${created.name}" created`)
    return created
  }

  const putView = async (view: View): Promise<View> => {
    const updated = await api.putView(view)
    ingestView(updated)
    return updated
  }

  const putViewSnapshot = async (id: string, image: Blob): Promise<View> => {
    const updated = await api.putViewSnapshot(id, image)
    ingestView(updated)
    return updated
  }

  const deleteView = async (id: string): Promise<void> => {
    await api.deleteView(id)
    removeView(id)
    notify('info', 'View removed')
  }

  const postViewCopy = async (from: { source_view_id?: string; template_id?: string; owner_id?: string }): Promise<View> => {
    const copy = await api.postViewCopy(from)
    ingestView(copy)
    notify('success', `View "${copy.name}" copied`)
    return copy
  }

  const getPageViews = async (pageId: string): Promise<PageView[]> => {
    try {
      const rows = await api.getPageViews(pageId)
      for (const sv of rows) ingestPageView(sv)
      return rows
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Failed to load page views')
      throw err
    }
  }

  const postPageView = async (pageId: string, input: Partial<PageView>): Promise<PageView> => {
    const created = await api.postPageView(pageId, input)
    ingestPageView(created)
    return created
  }

  const putPageView = async (sv: PageView): Promise<PageView> => {
    const updated = await api.putPageView(sv)
    ingestPageView(updated)
    return updated
  }

  const deletePageView = async (id: string): Promise<void> => {
    await api.deletePageView(id)
    removePageView(id)
  }

  return {
    isLoading, viewIndex, viewIds, pageViewIndex, pageViewIds, views, pageViews, dragId, dropBox,
    getViews, getView, postView, putView, putViewSnapshot, deleteView, postViewCopy,
    getPageViews, postPageView, putPageView, deletePageView
  }
})
